import { Mapbox, notifyAndFlyLayerId, airspaceClassLayerName } from 'constants/mapConstants';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	mapSourcesSelector,
	visibleMapLayersSelector,
	mapLoadingSelector,
	mapLayersSelector,
	selectedBasemapSelector,
	mapBasemapsSelector,
	geoJsonDataSelector
} from 'slice-reducers/mapSlice';
import { Layer, Source } from 'react-mapbox-gl';
import MapLayerControls from 'components/map/MapLayerControls';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { getMapData } from 'actions/map';
import { startGeoJsonPolling } from 'actions/map';
const Map = ({
	map,
	children,
	id,
	layerPickerPosition,
	styleLoadCallback,
	idle,
	setIdle,
	...rest
}) => {
	const dispatch = useDispatch();
	const location = useLocation();
	const visibleMapLayers = useSelector(visibleMapLayersSelector);
	const selectedBasemap = useSelector(selectedBasemapSelector);
	const mapBasemaps = useSelector(mapBasemapsSelector);
	const selectedStyleUrl = mapBasemaps.find(s => s.id === selectedBasemap)?.mapbox_path;
	const mapStyle = selectedStyleUrl ? selectedStyleUrl : 'mapbox://styles/mapbox/streets-v11';
	const sources = useSelector(mapSourcesSelector);
	// Memoize geoJsonSources to prevent unnecessary recalculations
	const geoJsonSources = useMemo(
		() => sources.filter(s => s.data_format === 'geojson'), //				//Notify & Fly layer refresh is handled by pusher

		[sources]
	);
	const mapLoading = useSelector(mapLoadingSelector);
	const disableMapLayerControls = !idle || mapLoading;
	const onNotifyRoute = location.pathname.includes('notify');
	const disabledLayerToggles = onNotifyRoute
		? [airspaceClassLayerName, notifyAndFlyLayerId]
		: [airspaceClassLayerName];
	const mapLayers = useSelector(mapLayersSelector);
	const geoJsonData = useSelector(geoJsonDataSelector);

	const [tileServerFetched, setTileServerFetched] = useState(false);

	const handleStyleLoad = map => {
		if (styleLoadCallback) {
			styleLoadCallback(map);
		}
	};

	useEffect(() => {
		if (mapLayers.length === 0 && !mapLoading && !tileServerFetched) {
			dispatch(getMapData());
			setTileServerFetched(true);
		}
	}, [dispatch, mapLayers, mapLoading, tileServerFetched]);

	// Start GeoJSON polling when component mounts
	useEffect(() => {
		let cleanup = null;

		if (geoJsonSources.length > 0) {
			cleanup = dispatch(startGeoJsonPolling(geoJsonSources));
		}

		return () => {
			if (cleanup) {
				cleanup();
			}
		};
	}, [dispatch, geoJsonSources]);

	return (
		<>
			<Mapbox {...rest} style={mapStyle} onStyleLoad={handleStyleLoad}>
				{sources.map(s => {
					if (s.data_format === 'geojson') {
						return (
							<Source
								key={s.handle}
								id={s.handle}
								geoJsonSource={{
									type: s.data_format,
									data: geoJsonData[s.handle] || { type: 'FeatureCollection', features: [] }
								}}
							/>
						);
					}
					if (
						s.data_format === 'vector' ||
						s.data_format === 'raster-dem' ||
						s.data_format === 'raster' ||
						s.data_format === 'raster-array'
					) {
						return (
							<Source
								key={s.handle}
								id={s.handle}
								tileJsonSource={{
									type: s.data_format,
									url: s.source_url
								}}
							/>
						);
					}

					return null;
				})}
				{visibleMapLayers.map(l => {
					const layersWithStyles = l.styles?.[selectedBasemap];
					if (!layersWithStyles) return null;
					return layersWithStyles.map((layerStyle, idx) => {
						if (!layerStyle || !layerStyle.type) return null;
						const layerId = `${l.id}-style-${idx}`;
						return (
							<Layer
								key={layerId}
								id={layerId}
								sourceId={l.source}
								sourceLayer={layerStyle['source-layer']}
								{...layerStyle}
							/>
						);
					});
				})}
				{children}
			</Mapbox>
			<MapLayerControls disabled={disableMapLayerControls} disabledToggles={disabledLayerToggles} />
		</>
	);
};

//This component also expects the props that react-mapbox-gl needs (see library documentation).

Map.propTypes = {
	showLayerPicker: PropTypes.bool,
	show3DToggle: PropTypes.bool,
	disabledToggles: PropTypes.arrayOf(PropTypes.string),
	idle: PropTypes.bool.isRequired,
	setIdle: PropTypes.func.isRequired
};

export default Map;
